import type {
  ActionConfig,
  ActionReference,
  TriggerConfig,
  LoopRecordListActionGroupConfig,
  TriggerReference,
  ValueConfig_V,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import type { Fields } from "@smartsuite/smartsuite-client";
import * as R from "remeda";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import { getOutputDescriptions } from "../../../../../../../../utils/conditions-coverter.hook";
import {
  generalTypeFilter,
  isSupportedOutput,
  isValidURL,
  memoizedConvertOutputToDynamicSectionOption,
  ssTypeFilter,
  systemTypeFilter,
} from "../../../../utils";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { findMatchedAction } from "../../../../../../../../utils/match-action";
import { t } from "i18next";
import type { LoopingOptionGroup } from "../../../../MemberEditor/utils";
import { rootStore } from "../../../../../../../../stores";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../../../../../../utils/constants";
import { createLoopItemPill } from "../../../../utils/createLoopItemPill";

const validateLinkValues = (values: ValueConfig_V[]): string => {
  if (
    values.some(
      (value) =>
        value.value.oneofKind === "fileUrl" && !isValidURL(value.value.fileUrl),
    )
  ) {
    return "The field is not valid";
  }
  return "";
};

export interface Option {
  title: string;
  icon: string;
  outputId: string;
}

interface LinkPill extends FieldTypePillProps {
  type: "triggerOutput" | "actionOutput" | "custom" | "loop";
  value: string;
  title: string;
  icon: string;
  isError: boolean;
}

export const triggerToOptions = (
  trigger: TriggerCandidate,
  generalTypes?: ValueDescription_T[],
  ssTypes?: ValueDescription_T[],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): Option[] =>
  getOutputDescriptions(trigger)
    .filter((output) => {
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }
      if (output.smartsuiteFieldType !== undefined) {
        return ssTypeFilter(
          output.smartsuiteFieldType,
          ssFieldTypes,
          ssTypes,
          output,
        );
      }

      return generalTypeFilter(generalTypes, output);
    })
    .map((output) => {
      return {
        title: output.label,
        outputId: output.outputId,
        icon: output.icon,
      };
    });

interface ConvertersFunctions {
  convertInputToLinkPills: (
    input: InputCandidate,
    triggerOptions: Option[],
    actionOptions: Option[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ) => LinkPill[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();

  const convertInputToLinkPills = (
    input: InputCandidate,
    triggerOptions: Option[],
    actionOptions: Option[],
    trigger: TriggerConfig,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ): LinkPill[] => {
    const rootLooping = rootStore.automations.candidate.looping;
    const looping = currentAction
      ? (rootLooping?.scopeForAction?.(currentAction.hash) ?? rootLooping)
      : rootLooping;

    return (
      input.value?.values.flatMap((value): LinkPill[] => {
        switch (value.value.oneofKind) {
          case "reference":
            if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
              return [
                {
                  ...(createLoopItemPill(looping) as LinkPill),
                  tooltip: makeCurrentItemPillTooltip(looping),
                },
              ];
            }
            if (value.value.reference.source.oneofKind === "triggerReference") {
              const { outputId } = value.value.reference;

              let triggerOption = triggerOptions.find(
                (option) => option.outputId === outputId,
              );

              if (triggerOption === undefined && loopOptions?.options.length) {
                triggerOption = loopOptions.options.find(
                  (option) => option.outputId === outputId,
                ) as Option;
              }

              return [
                {
                  type: "triggerOutput",
                  value: JSON.stringify({
                    value: outputId,
                    target: "trigger",
                  }),
                  icon: triggerOption?.icon ?? "",
                  isError: !triggerOption?.title,
                  title:
                    triggerOption?.title ??
                    String(t("automation-form.deleted-field-title")),
                  tooltip: makeTriggerPillTooltip(trigger),
                },
              ];
            } else if (
              value.value.reference.source.oneofKind === "actionReference"
            ) {
              const { outputId } = value.value.reference;
              const actionOption = actionOptions.find(
                (option) => option.outputId === outputId,
              );
              const pillAction = findMatchedAction(
                value.value.reference.source,
                actions,
              );

              const isSupported =
                pillAction === undefined
                  ? false
                  : isSupportedOutput(
                      pillAction,
                      actions,
                      currentAction,
                      looping,
                    );

              if (
                value.value.reference.outputId.startsWith("currentItem|") ||
                value.value.reference.outputId.startsWith("currentItem.")
              ) {
                const outputOptions =
                  memoizedConvertOutputToDynamicSectionOption(
                    looping,
                    rootStore.automations.candidate.allActions,
                    rootStore.automations.candidate.self.trigger,
                  );

                const outputOption = outputOptions?.options.find(
                  (option) => option.outputId === outputId,
                );

                return [
                  {
                    type: "actionOutput",
                    title:
                      outputOption?.label ??
                      String(t("automation-form.deleted-field-title")),
                    icon: outputOption?.icon ?? "",
                    isError: !isSupported || !outputOption?.label,
                    value: JSON.stringify({
                      value: outputId,
                      target: "action",
                      // @ts-expect-error
                      hash: pillAction?.hash,
                    }),
                    tooltip:
                      pillAction === undefined
                        ? undefined
                        : makeActionPillTooltip(pillAction),
                  } as const,
                ];
              }

              return [
                {
                  type: "actionOutput",
                  value: JSON.stringify({
                    value: outputId,
                    target: "action",
                    // @ts-expect-error
                    hash: pillAction?.hash,
                  }),
                  icon: actionOption?.icon ?? "",
                  title:
                    actionOption?.title ??
                    String(t("automation-form.deleted-field-title")),
                  isError: !isSupported || !actionOption?.title,
                  tooltip:
                    pillAction === undefined
                      ? undefined
                      : makeActionPillTooltip(pillAction),
                },
              ];
            } else {
              return [];
            }
          case "fileUrl": {
            const { fileUrl } = value.value;
            return [
              {
                type: "custom",
                value: fileUrl,
                title: fileUrl,
                icon: "",
                isError: !isValidURL(fileUrl),
              },
            ];
          }
          default:
            return [];
        }
      }) ?? []
    );
  };

  return {
    convertInputToLinkPills,
  };
};

export const mergeNextPillToInput = (
  input: InputCandidate,
  pill:
    | {
        type: "triggerOutput";
        triggerReference: TriggerReference;
        outputId: string;
      }
    | {
        type: "actionOutput";
        actionReference: ActionReference;
        outputId: string;
      }
    | {
        type: "custom";
        value: string;
      }
    | { type: "loop"; source: LoopRecordListActionGroupConfig["source"] },
): InputCandidate => {
  const newInput = {
    ...input,
    value: {
      values: [
        ...(R.isTruthy(input.value) ? input.value.values : []),
        ...(() => {
          switch (pill.type) {
            case "triggerOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "triggerReference",
                        triggerReference: pill.triggerReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "actionOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "actionReference",
                        actionReference: pill.actionReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "custom": {
              return [
                {
                  value: {
                    oneofKind: "fileUrl",
                    fileUrl: pill.value,
                  } as const,
                },
              ];
            }
            case "loop":
              if (pill.source.oneofKind === "outputReference") {
                return [
                  {
                    value: {
                      oneofKind: "reference",
                      reference: pill.source.outputReference,
                    } as const,
                  },
                ];
              }

              return [];
            default:
              return [];
          }
        })(),
      ],
    },
  };
  newInput.validationError = validateLinkValues(newInput.value.values);
  return newInput;
};

export const ejectPillFromInput = (
  input: InputCandidate,
  index: number,
): InputCandidate => {
  const newInput = {
    ...input,
    value: { values: input.value?.values.filter((_, i) => i !== index) ?? [] },
  };
  newInput.validationError = validateLinkValues(newInput.value.values);
  return newInput;
};
